import { useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { FormEvent, useEffect, useState } from "react";
import { useUserRegister, useVerifyEmail } from "@/features/auth/repositories/auth-repository";
import { registrationStore } from "@/features/auth/store";

import Button from "@/components/buttons/Button";
import Label from "@/components/form-controls/Label";
import OtpInput from "@/components/form-controls/OtpInput";
import RegisterHeader from "@/features/auth/fragments/registration-header";
import { base64ToFile } from "@/utils/conversion";
import toast from "react-hot-toast";


const RegisterVerificationForm = () => {
    const navigate = useNavigate();
    const store = useSnapshot(registrationStore);

    const [token, setToken] = useState<string>();

    const { mutate, isPending } = useUserRegister(
        () => navigate("/auth/register/welcome")
    );

    const resendMutation = useVerifyEmail();

    const submit = async (event: FormEvent) => {
        event.preventDefault();

        if (!token || token.length < 6) {
            toast.error("please enter the 6-digit verification code");
            return;
        }

        if (!(store.personal && store.business && store.identification)) {
            toast.error("some registration details are missing, please go back and complete them");
            return;
        }

        const formData = new FormData();

        // Handle optional avatar file
        if (store.identification.avatarFile) {
            const imageFile = await base64ToFile(store.identification.avatarFile, "avatar-file")
            formData.append("profile", imageFile);
        }
        
        const idFile = await base64ToFile(store.identification.idFile, "id-file");

        formData.append("firstName", store.personal.firstName);
        formData.append("lastName", store.personal.lastName);
        formData.append("email", store.personal.email);
        formData.append("password", store.personal.password);
        formData.append("salutation",store.personal.salutation);
        formData.append("phoneNumber", store.personal.phone);
        
        // Handle optional date of birth
        if (store.identification.dateOfBirth) {
            formData.append("dateOfBirth", store.identification.dateOfBirth);
        }
        
        formData.append("nationality", store.identification.nationality);
        formData.append("identificationType", store.identification.idType);
        formData.append("identificationNumber", store.identification.idNumber);
        formData.append("identificationImages", idFile);
        formData.append("business.BusinessName", store.business.businessName);
        
        // Handle optional trading name
        if (store.business.tradingName) {
            formData.append("business.businessTradingName", store.business.tradingName);
        }
        
        // Handle optional TIN number
        if (store.business.tinNumber) {
            formData.append("business.businessTinNumber", store.business.tinNumber.toString());
        }
        
        formData.append("business.businessRegion", store.business.region);
        formData.append("business.businessDistrict", store.business.district);
        
        // Handle optional street
        if (store.business.street) {
            formData.append("business.businessStreet", store.business.street);
        }
        
        formData.append("business.businessManager", store.business.managerName);
        formData.append("business.businessEmail", store.business.managerEmail);
        formData.append("business.businessPhone", store.business.managerPhone);
        
        // Handle optional GPS coordinates
        if (store.business.latitude) {
            formData.append("business.businessLat", store.business.latitude);
        }
        if (store.business.longitude) {
            formData.append("business.businessLng", store.business.longitude);
        }

        mutate(formData)
    }

    useEffect(() => {
        token && store.setToken(token);
    }, [token])

    return (
        <div className="space-y-12">
            <RegisterHeader 
                heading={"Verify Your Email"}
                description={`We sent a code to ${store.personal?.email ?? "your email"}`}
            />

            <form className="lg:w-3/5 mx-auto" onSubmit={submit}>
                <Label 
                    name="otp" 
                    text="Enter Verification Code" 
                    className="mb-2"
                />
                <OtpInput 
                    inputCount={6} 
                    onChange={(value) => setToken(value)}                    
                />
                <div className="text-sm text-zinc-400 mt-4 mb-12">
                    <span>
                        Didn't get the code?
                    </span>
                    <button
                        type="button"
                        onClick={() => store.personal?.email && resendMutation.mutate(store.personal.email)}
                        disabled={resendMutation.isPending || !store.personal?.email}
                        className="text-primary underline ml-1 disabled:opacity-50"
                    >
                        Click to resend
                    </button>
                </div>

                <Button
                    type="submit"
                    intent="primary" 
                    size="md" 
                    className="w-full"
                    loading={isPending}
                >
                    Continue
                </Button>
            </form>
        </div>
    )
}

export default RegisterVerificationForm